import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "../../../../../i18n/useTranslation";
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  FlatList,
  Image,
  SafeAreaView,
  Platform,
  type LayoutChangeEvent,
} from "react-native";
import { postFeeLookup } from "../../../../../services/common.service";
import { useSessionStore } from "../../../../../../../state/src/useSessionStore";
import type { SessionState } from "../../../../../../../state/src/useSessionStore";
import { ProductType } from "../../../../../view-models/productType";
import type { FeeInfo } from "../../../../../view-models/feeInfo";
import { FeeLookupHelper } from "../../../../../helper/feeLookupHelper";
import { useGlobalError }  from '../../../../uiComponents/GlobalErrorProvider';
import Colors from "../../../../../constants/colors";
import { ComponentStyles } from "../../../../../constants/componentStyles";
import RadioSelectedIcon from "../../../../../../../../assets/images/RadioSelectButton.svg";
import { capturePosthogEvent } from "../../../../../utils/analytics";
import RightArrowIcon from "../../../../../../../../assets/images/chevron-right.svg";
import BankIcon from "../../../../../../../../assets/images/BankIcon.svg";
import LocationIcon from "../../../../../../../../assets/images/LocationCash.svg";
import WalletIcon from "../../../../../../../../assets/images/WalletIcon.svg";
import HomeIcon from "../../../../../../../../assets/images/HomeIcon.svg";
import HowTheyReceiveModal from "../../../common/HowTheyReceiveModal";

// Server-driven options will be shown here

type HowTheyReceiveStep = {
  /** Parent passes `handleNext` — call after user confirms a delivery option (list or modal). */
  onCountrySelected?: () => void;
  onValidationChange?: (isValid: boolean) => void;
  testID?: string | undefined;
};

export interface AgentItem {
  AgentLogos?: any;
  ExchangeRate: number;
  Fee: number;
  Raw: any;
  ReceiveAgentAbbreviation: string;
  ReceiveAgentId: string;
  ReceiveAgentName: string;
}

export default function HowTheyReceiveStep({
  onCountrySelected,
  onValidationChange,
  testID,
}: HowTheyReceiveStep) {
  // const { t, i18n } = useTranslation();
  const { t, i18n } = useTranslation(['translation', 'delivery']);
  const { showError } = useGlobalError();
  const [isWalletVisible, setIsWalletVisible] = React.useState(false);
  const [isCashPickUp, setIsCashPickUp] = useState(false);
  const [modalTitle, setModalTitle] = useState('Mobile Wallet')
  const [modalSubTitle, setModalSubTitle] = useState('Subtitle');
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [selectedWallet, setSelectedWallet] = useState([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [feeInfos, setFeeInfos] = useState<FeeInfo[]>([]);
  /**
   * Web only: title is `position: fixed`, so the list needs top padding ≥ title block height.
   * Measured on layout so long / wrapped titles (e.g. Spanish) are not covered by the first row.
   */
  const [webListTopPadding, setWebListTopPadding] = useState(112);
  const agentId = useSessionStore((s: SessionState) => s.agentId);
  const unitProfileId = useSessionStore((s: SessionState) => s.unitProfileId);
  const selectedDestinationCountryCode = useSessionStore((s: SessionState) => s.selectedDestinationCountryCode);
  const selectedDestinationStateAbbr = useSessionStore((s: SessionState) => s.selectedDestinationStateAbbr);
  const selectedReceiveCurrency = useSessionStore((s: SessionState) => (s as any).selectedReceiveCurrency);
  const setSelectedSendOption = useSessionStore((s: SessionState) => (s as any).setSelectedSendOption);
  const setPendingSendOption = useSessionStore((s: SessionState) => (s as any).setPendingSendOption);
  const pendingSendOption = useSessionStore((s: SessionState) => (s as any).pendingSendOption);
  const selectedSendOption = useSessionStore((s: SessionState) => (s as any).selectedSendOption);
  const setSelectedDeliveryOptionStore = useSessionStore((s: SessionState) => (s as any).setSelectedDeliveryOptionStore);
  const lastFeeLookupResponse = useSessionStore((s: SessionState) => (s as any).lastFeeLookupResponse);
  const selectedLanguage = useSessionStore((s: SessionState) => (s as any).selectedLanguage);

  const colors = ["#FF6B6B", "#4ECDC4", "#FFD93D", "#6A4C93", "#1A535C"];
  const randomColor = (index: number) => colors[index % colors.length];

  const isValid = selected != null ;
      
    useEffect(() => {
      if (onValidationChange) {
        onValidationChange(isValid);
      }
    }, [isValid, onValidationChange]);

  /** Parent `handleNext` requires `isStepValid`; set explicitly before advancing. */
  const scheduleAdvanceToNextStep = () => {
    onValidationChange?.(true);
    setTimeout(() => {
      onCountrySelected?.();
    }, 0);
  };

  const getOptionKey = (fee: FeeInfo): string => {
    return JSON.stringify({
      sendCurrency: fee?.SendAmounts?.SendCurrency || null,
      exchangeRate: FeeLookupHelper.getExchangeRate(fee) || null,
      receiveCurrency: FeeLookupHelper.getReceiveCurrency(fee) || null,
      deliveryOptDisplayName: fee.DeliveryOptDisplayName || null,
      receiveAgentId: (fee as any).ReceiveAgentId || null,
    });
  };

  /** Stable list key / selection compare for both raw FeeInfo rows and grouped rows. */
  const getRowListKey = (item: any): string => {
    if (item?.type === "grouped" && item?.agents?.[0]?.Raw) {
      return getOptionKey(item.agents[0].Raw);
    }
    return getOptionKey(item);
  };

  const formatExchangeRate = (fee: FeeInfo): string => {
    const sendCurrency = fee?.SendAmounts?.SendCurrency || 'USD';
    const rate = FeeLookupHelper.getExchangeRate(fee);
    const receiveCurrency = FeeLookupHelper.getReceiveCurrency(fee);
    const rateNum = Number(rate);
    const rateFixed = isFinite(rateNum) ? rateNum.toFixed(4) : String(rate);
    return `1 ${sendCurrency} = ${rateFixed} ${receiveCurrency}`;
  };

  const formatFee = (fee: FeeInfo): string => {
    const amount = Number(fee?.SendAmounts?.TotalSendFees || 0);
    const currency = fee?.SendAmounts?.SendCurrency || 'USD';
    return `${amount.toFixed(2)} ${currency}`;
  };

  // Utility function to group by a "composite key"
function groupBy<T, K>(array: T[], keyGetter: (item: T) => string): Map<string, T[]> {
  const map = new Map<string, T[]>();
  array.forEach((item) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

  useEffect(() => {
    const useOrFetch = async () => {
      if (!agentId || !unitProfileId || !selectedDestinationCountryCode) return;
      try {
        setIsLoading(true);
        setError(null);
        let result: any = lastFeeLookupResponse;
        if (!result) {
          const payload: any = {
            agentID: agentId,
            unitProfileID: unitProfileId,
            amount: 1,
            sendCurrency: 'USD',
            destinationCountry: selectedDestinationCountryCode,
            productType: ProductType.SEND,
            allOptions: true,
          };
          if ((selectedDestinationCountryCode === 'USA' || selectedDestinationCountryCode === 'CAN') && selectedDestinationStateAbbr) {
            payload.destinationState = selectedDestinationStateAbbr;
          }
          result = await postFeeLookup(payload);
        }

        // Unwrap nested data if needed and derive array
        let inner: any = result;
        if (typeof result?.data === 'string') {
          try { inner = JSON.parse(result.data); } catch { inner = result; }
        } else if (typeof result?.data === 'object') {
          inner = result.data;
        }
        const arr: FeeInfo[] = Array.isArray(inner?.FeeInfos) ? inner.FeeInfos : [];

        // Build sendOptions from parsed array
        const sendOptions: FeeInfo[] = (() => {
          // Prefer first non-USD receive currency from the response
          const nonUsdCurrency = (() => {
            const first = arr.find((f: FeeInfo) => {
              const rc = (FeeLookupHelper.getReceiveCurrency(f) || '').toUpperCase();
              return rc && rc !== 'USD';
            });
            return first ? (FeeLookupHelper.getReceiveCurrency(first) || '').toUpperCase() : '';
          })();

          const filtered = arr.filter(
            (fee: FeeInfo) =>
              fee.ErrorInfo == null &&
              fee.SendAmounts != null &&
              (!nonUsdCurrency || (FeeLookupHelper.getReceiveCurrency(fee) || '').toUpperCase() === nonUsdCurrency)
          );

          const grouped = groupBy(filtered, (f: FeeInfo) =>
            JSON.stringify({
              sendCurrency: f.SendAmounts!.SendCurrency,
              exchangeRate: FeeLookupHelper.getExchangeRate(f),
              receiveCurrency: FeeLookupHelper.getReceiveCurrency(f),
              deliveryOptDisplayName: f.DeliveryOptDisplayName,
              totalSendFees: f.SendAmounts!.TotalSendFees,
              receiveAgentName: f.ReceiveAgentName,
            })
          );

          return Array.from(grouped.values()).map((group) => group[0]);
        })();
        setFeeInfos(sendOptions);
      } catch (e: any) {
        console.error('FeeLookup failed in HowTheyReceiveStep:', e);
        setError('Failed to load delivery options');
      } finally {
        setIsLoading(false);
      }
    };
    useOrFetch();
  }, [agentId, unitProfileId, selectedDestinationCountryCode, selectedDestinationStateAbbr, lastFeeLookupResponse]);

  const groupedData = useMemo(() => {
    const cashPickupGroup: FeeInfo[] = [];
    const walletGroup: FeeInfo[] = [];
    const accountDepositGroup: FeeInfo[] = [];
    const singles: FeeInfo[] = [];

    feeInfos.forEach((item: any) => {
      // const name = item?.DeliveryOptDisplayName.toLowerCase();

      // if (name.includes("cash pickup")) {
      //   cashPickupGroup.push(item);
      // } else if (name.includes("send to wallet")) {
      //   walletGroup.push(item);
      // } else {
      //   singles.push({ ...item, type: "single" });
      // }

      const option = item?.DeliveryOption;

      if (option === 'WILL_CALL' || option === 'WILLCALL_TO') {
        cashPickupGroup.push(item);
      } else if (option === 'DIRECT_TO_ACCT') {
        walletGroup.push(item);
      } else if (option === 'BANK_DEPOSIT') {
        accountDepositGroup.push(item);
      } else {
        singles.push({ ...item, type: 'single' });
      }
    });

    // Convert each group into grouped structure
    const makeGroup = (groupArray: any) => {
      if (groupArray.length <= 1) {
        // Single item (rare case)
        return { ...groupArray[0], type: "single" };
      }

      const first = groupArray[0];

      return {
        type: "grouped",
        DeliveryOptDisplayName: first.DeliveryOptDisplayName,
        DeliveryOption: first.DeliveryOption,
        DestinationCountry: first.DestinationCountry,
        Fee: first.EstimatedReceiveAmount,
        ExchangeRate: first.ValidExchangeRate,
        agents: groupArray.map((g: any) => ({
          ReceiveAgentAbbreviation: g.ReceiveAgentAbbreviation,
          ReceiveAgentName: g.ReceiveAgentName,
          ReceiveAgentId: g.ReceiveAgentId,
          AgentLogos: g.AgentLogos,
          Fee: g.EstimatedReceiveAmount,
          ExchangeRate: g.ValidExchangeRate,
          Raw: g,
        })),
      };
    };

    const result = [];

    if (cashPickupGroup?.length) result.push(makeGroup(cashPickupGroup));
    if (walletGroup?.length) result.push(makeGroup(walletGroup));
    if (accountDepositGroup?.length) result.push(makeGroup(accountDepositGroup));

    return [...result, ...singles];
  }, [feeInfos]);

  /**
   * Restore selected row from store when user returns to this step (e.g. back from Review).
   * Reopen the partner modal only when there is a committed `selectedSendOption` and the choice
   * was a specific agent in a grouped row — avoids reopening during an in-flight modal pick.
   */
  const modalAutoOpenedThisMountRef = useRef(false);
  useEffect(() => {
    const savedForHighlight = selectedSendOption ?? pendingSendOption;
    if (!savedForHighlight || feeInfos.length === 0) return;

    const key = getOptionKey(savedForHighlight);
    setSelected(key);

    if (modalAutoOpenedThisMountRef.current) return;
    if (!selectedSendOption) return;

    const committedKey = getOptionKey(selectedSendOption);
    if (committedKey !== key) return;

    for (const row of groupedData) {
      if (row?.type !== "grouped" || !row.agents?.length) continue;
      const agentMatch = row.agents.find((a: any) => getOptionKey(a.Raw) === key);
      if (!agentMatch) continue;

      modalAutoOpenedThisMountRef.current = true;
      setSelectedWallet(row.agents);
      const name = row.DeliveryOptDisplayName?.toLowerCase?.() ?? "";
      const isCash =
        name.includes("cash pickup") ||
        row.DeliveryOption === "WILL_CALL" ||
        row.DeliveryOption === "WILLCALL_TO";
      const isWallet =
        name.includes("send to wallet") || row.DeliveryOption === "DIRECT_TO_ACCT";
      const isAccountDeposit =
        row.DeliveryOption === "BANK_DEPOSIT" || name.includes("account deposit");
      if (isCash) {
        setIsCashPickUp(true);
        setModalTitle(t("delivery:CASH_PICKUP_TITLE"));
        setModalSubTitle(t("delivery:CASH_PICKUP_SUBTITLE"));
      } else if (isAccountDeposit) {
        setIsCashPickUp(false);
        setModalTitle(t("delivery:BANK_DEPOSIT"));
        setModalSubTitle("");
      } else {
        setIsCashPickUp(false);
        setModalTitle(t("delivery:MOBILE_WALLET_TITLE"));
        setModalSubTitle("");
      }
      setIsWalletVisible(true);
      break;
    }
  }, [feeInfos, groupedData, pendingSendOption, selectedSendOption, t]);

  const filteredAgents = useMemo(() => {
    const q = search.toLowerCase();

    return selectedWallet.filter((item: any) => {
      const name =
        item?.ReceiveAgentName ||
        item?.Raw?.ReceiveAgentName ||
        "";
      return name.toLowerCase().includes(q);
    });
  }, [selectedWallet, search]);

  const onSelectWalletOption = (item: any) => {
    const key = getOptionKey(item?.Raw);
    setSelected(key);
    setPendingSendOption(item?.Raw);
    try {
      setSelectedDeliveryOptionStore?.({
        DeliveryOption:
        (item?.Raw as any)?.DeliveryOption ??
        (item?.Raw as any)?.DeliveryOptId ??
        null,
         option: item?.Raw,
      });
    } catch {}
    setTimeout(() => {
      setIsWalletVisible(false);
      setIsCashPickUp(false);
    }, 200);
    scheduleAdvanceToNextStep();
  };

  const oncloseModal = () => {
    setIsWalletVisible(false), setIsCashPickUp(false)
  }

  const getTranslatedDeliveryName = (item: any): string => {
    const key =
      item?.DeliveryOption ||
      item?.DeliveryOptId ||
      item?.DeliveryOptDisplayName;

    return t(`delivery:${key}`, {
      defaultValue:
        item?.DeliveryOptDisplayName ||
        item?.ReceiveAgentName ||
        t('RECEIVE_METHOD_DELIVERY_OPTION'),
    });
  };

  const getDeliveryTranslationKey = (item: any) => {
    return (
      item?.DeliveryOption ||
      item?.DeliveryOptId ||
      item?.DeliveryOptDisplayName
    );
  };
  const renderHowTheyReceiveOption = (item: any) => {
    const key = getRowListKey(item);
    const isGroupRowSelected =
      item?.type === "grouped" &&
      !!selected &&
      !!item?.agents?.some((a: any) => getOptionKey(a.Raw) === selected);
    const isSelected = isGroupRowSelected || selected === key;
    const optionType = item?.DeliveryOptDisplayName?.toLowerCase?.();

    // const isSpecial =
    //   optionType?.includes("send to wallet") ||
    //   optionType?.includes("cash pickup");
    const isSpecial = item?.type === 'grouped' ? true : false;

    // 👇 Special layout for "Cash at location" and "Mobile wallet"
    if (isSpecial) {
      return (
        <TouchableOpacity
          testID={`deliveryOption-${key}`}
          style={[styles.specialRow, isSelected && styles.specialRowSelected]}
          onPress={() => {
            const name = item?.DeliveryOptDisplayName?.toLowerCase?.() ?? '';
            const isCash =
              name.includes('cash pickup') ||
              item?.DeliveryOption === 'WILL_CALL' ||
              item?.DeliveryOption === 'WILLCALL_TO';
            const isWallet =
              name.includes('send to wallet') || item?.DeliveryOption === 'DIRECT_TO_ACCT';
            const isAccountDeposit =
              item?.DeliveryOption === 'BANK_DEPOSIT' || name.includes('account deposit');

            if (isCash || isWallet || isAccountDeposit) {
              setIsWalletVisible(true);
              if (isCash) {
                setIsCashPickUp(true);
                setModalTitle(t('delivery:CASH_PICKUP_TITLE'));
                setModalSubTitle(t('delivery:CASH_PICKUP_SUBTITLE'));
              } else if (isAccountDeposit) {
                setIsCashPickUp(false);
                setModalTitle(t('delivery:BANK_DEPOSIT'));
                setModalSubTitle('');
              } else {
                setIsCashPickUp(false);
                setModalTitle(t('delivery:MOBILE_WALLET_TITLE'));
                setModalSubTitle('');
              }
              setSelectedWallet(item?.agents);
            }
          }}
        >
          <View style={styles.specialRowIcon}>
            {item?.DeliveryOption === 'BANK_DEPOSIT' ? (
              <BankIcon width={24} height={24} />
            ) : item.DeliveryOptDisplayName?.toLowerCase?.() === 'send to wallet' ? (
              <WalletIcon width={24} height={24} />
            ) : (
              <LocationIcon width={24} height={24} />
            )}
          </View>
          <View style={styles.specialRowTextColumn}>
            {/* <Text style={styles.methodTitle}>
              {item.DeliveryOptDisplayName ||
                item.ReceiveAgentName ||
                t("RECEIVE_METHOD_DELIVERY_OPTION")}
            </Text> */}
            <Text style={styles.methodTitle}>
              {getTranslatedDeliveryName(item)}
            </Text>

            {/* <View style={styles.rateRow}>
              <Text style={styles.methodDetails}>{formatExchangeRate(item)}</Text>
              <View style={styles.dotStyle} />
              <Text style={styles.methodDetails}>{formatFee(item)}</Text>
              <View style={styles.dotStyle} />
              <Text style={styles.methodDetails}>
                {item.ReceiveAgentAbbreviation ?? "Instant"}
              </Text>
            </View> */}
          
            <View style={styles.providersRow}>
              {item?.agents?.slice(0, 3).map((i: any, index: number) => (
                <View
                  key={index}
                  style={[
                    styles.providerCircle,
                    { backgroundColor: randomColor(index) },
                  ]}
                />
              ))}
              {item?.agents?.length > 3 && (
                <View style={styles.moreCircle}>
                  <Text style={{ color: "#fff", fontSize: 12 }}>
                    +{item?.agents?.length - 3}
                  </Text>
                </View>
              )}
            </View>
          </View>

          {/* Right arrow / indicator */}
          <View style={styles.specialRowChevron}>
            <RightArrowIcon width={24} height={24} />
          </View>
        </TouchableOpacity>
      );
    }

    // 👇 Default layout for other options
    return (
      <TouchableOpacity
        testID={`deliveryOption-${key}`}
        style={styles.methodRow}
        onPress={() => {
          setSelected(key);
          setPendingSendOption(item);
          try {
            setSelectedDeliveryOptionStore?.({
              DeliveryOption:
                (item as any)?.DeliveryOption ??
                (item as any)?.DeliveryOptId ??
                null,
              option: item,
            });
          } catch {}
          scheduleAdvanceToNextStep();
        }}
      >
        <View style={styles.methodRowIcon}>
          {item.DeliveryOptDisplayName?.toLowerCase?.() === "account deposit" ? (
            <BankIcon width={24} height={24} />
          ) : item.DeliveryOptDisplayName?.toLowerCase?.().includes("cash pickup") ? (
            <LocationIcon width={24} height={24} />
          ) : (
            <HomeIcon width={24} height={24} />
          )}
        </View>
        <View style={styles.methodRowTextColumn}>
          {/* <Text style={styles.methodTitle}>
            {item.DeliveryOptDisplayName ||
              item.ReceiveAgentName ||
              t("RECEIVE_METHOD_DELIVERY_OPTION")}
          </Text> */}
          <Text style={styles.methodTitle}>
            {t(`delivery:${getDeliveryTranslationKey(item)}`, {
              defaultValue:
                item.DeliveryOptDisplayName ||
                item.ReceiveAgentName ||
                t("RECEIVE_METHOD_DELIVERY_OPTION"),
            })}
          </Text>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            <Text style={styles.methodDetails}>{formatExchangeRate(item)}</Text>
            <View style={styles.dotStyle} />
            <Text style={styles.methodDetails}>{formatFee(item)}</Text>
            {!!item.ReceiveAgentAbbreviation && (
              <>
                <View style={styles.dotStyle} />
                <Text style={styles.methodDetails}>
                  {item.ReceiveAgentAbbreviation}
                </Text>
              </>
            )}
          </View>
        </View>
        <View style={styles.radioButton}>
          {isSelected && (
            <RadioSelectedIcon width={20} height={20}/>
          )}
        </View>
      </TouchableOpacity>
    );
  }

  const listFooter = (
    <>
      {
        i18n.language === 'en' ? (
          <Text style={styles.methodDetails}>
            <Text style={{ fontWeight: '700' }}>Transfer speed </Text>
            depends on the delivery method, transaction review, and system availability of MoneyGram and its partners.
          </Text>
        ) : (
          <Text style={styles.methodDetails}>{t("HOW_THEY_RECEIVE_INFO")}</Text>
        )
      }
    </>
  );

  const onTitleSectionLayout = React.useCallback((e: LayoutChangeEvent) => {
    if (Platform.OS !== "web") return;
    const h = e.nativeEvent.layout.height;
    if (!h) return;
    const next = Math.ceil(h) + 8;
    setWebListTopPadding((prev) => (Math.abs(prev - next) < 2 ? prev : next));
  }, []);

  return (
    <View style={styles.container} testID={testID}>
      <View
        style={[styles.titleSection, Platform.OS === 'web' && styles.titleSectionFixedWeb]}
        onLayout={onTitleSectionLayout}
      >
        <Text style={styles.title}>{t("RECEIVE_METHOD_TITLE")}</Text>
        {isLoading && (
          <Text style={{ marginBottom: 8 }}>{t("RECEIVE_METHOD_LOADING")}</Text>
        )}
        {error && (
          <>
            <Text style={{ marginBottom: 8, color: 'red' }}>{error}</Text>
            <Text style={{ marginBottom: 8, color: 'red' }}>{t("RECEIVE_METHOD_ERROR")}</Text>
          </>
        )}
      </View>
      <FlatList
        testID="receiveMethodList"
        data={groupedData}
        keyExtractor={(item) => getRowListKey(item)}
        style={styles.list}
        contentContainerStyle={[
          styles.listContent,
          Platform.OS === 'web' && { paddingTop: webListTopPadding },
        ]}
        renderItem={({ item }) => (
          <View>
            {renderHowTheyReceiveOption(item)}
          </View>
        )}
        ListFooterComponent={listFooter}
        ListEmptyComponent={!isLoading && !error ? (
          <Text style={styles.emptyText}>{t("RECEIVE_METHOD_NO_OPTIONS")}</Text>
        ) : null}
        showsVerticalScrollIndicator={true}
        keyboardShouldPersistTaps="handled"
      />
      <HowTheyReceiveModal
        visible={isWalletVisible}
        isCashPickUp={isCashPickUp}
        onClose={oncloseModal}
        title={modalTitle}
        subTitle={modalSubTitle}
        searchPlaceHolder={t("COUNTRY_STEP_SEARCH_PLACEHOLDER")}
        searchValue={search}
        onSearchChangeText={setSearch}
        filteredData= {filteredAgents}//{selectedWallet}
        howTheyReceiveModalTestID="sendToWallet"
        onSelectWalletOption={onSelectWalletOption}
        selected={selected}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    paddingHorizontal: 24,
    backgroundColor: Colors.white,
    ...(Platform.OS === 'web' && { overflow: 'hidden' as any }),
  },
  titleSection: {
    flexShrink: 0,
    paddingBottom: 8,
  },
  /** Web: pin title to viewport so it never scrolls; top aligned to just below header/progress bar (no extra gap). */
  titleSectionFixedWeb: {
    position: 'fixed' as any,
    top: 80 as any,
    left: 24 as any,
    right: 24 as any,
    zIndex: 5 as any,
    backgroundColor: Colors.white,
    paddingBottom: 8,
    overflow: "visible" as any,
  },
  list: {
    flex: 1,
    minHeight: 0,
    alignSelf: 'stretch',
    ...(Platform.OS === 'web' && { overflow: 'auto' as any, minHeight: 0 as any }),
  },
  listContent: {
    paddingBottom: 32,
  },
  emptyText: {
    color: '#6B7280',
    marginTop: 8,
  },
  title: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.titleFont,
    fontWeight: '500',
    color: Colors.titleTextColor,
    lineHeight: 34,
    marginBottom: 8,
    ...(Platform.OS === "web" && { width: "100%" as any }),
  },
  subtitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    lineHeight: 24,
    color: Colors.subTitleColor,
    marginBottom: 24
  },
  methodRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 14,
    // borderBottomWidth: 1,
    // borderBottomColor: "#E5E7EB",
  },
  dotStyle: {
    height: 3,
    width: 3,
    borderRadius: 50,
    backgroundColor: Colors.titleTextColor,
    marginHorizontal: 4
  },
  methodTitle: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: ComponentStyles.subTitleFont,
    fontWeight: '400',
    /** Room for two lines (e.g. Spanish cash pickup) without overlap on web */
    lineHeight: 24,
    color: Colors.titleTextColor,
    marginBottom: 4,
    flexShrink: 1,
  },
  /** flex:1 text column — minWidth:0 so wrapped labels measure correctly on web */
  specialRowIcon: {
    alignSelf: "center",
  },
  specialRowChevron: {
    alignSelf: "center",
  },
  specialRowTextColumn: {
    flex: 1,
    marginLeft: 12,
    marginRight: 8,
    minWidth: 0,
    alignSelf: "stretch",
  },
  methodRowIcon: {
    alignSelf: "center",
  },
  methodRowTextColumn: {
    flex: 1,
    marginLeft: 12,
    marginRight: 10,
    minWidth: 0,
    alignSelf: "stretch",
  },
  methodDetails: {
    fontFamily: ComponentStyles.textFamily,
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 16,
    color: Colors.titleTextColor,
    // marginBottom: 4
  },
  providersRow: { flexDirection: "row", marginTop: 6},
  providerCircle: { height: 24, width: 24, borderRadius: 50 },
  moreCircle: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: "#6B7280",
    alignItems: "center",
    justifyContent: "center",
  },
  radioOuter: {
    width: 20,
    height: 20,
    borderRadius: 10,
    borderWidth: 2,
    borderColor: "#D1D5DB",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: 8,
    marginTop: 4,
  },
  radioOuterSelected: { borderColor: "#000" },
  radioInner: { width: 10, height: 10, borderRadius: 5, backgroundColor: "#000" },
  footer: {
    position: "absolute",
    left: 0,
    right: 0,
    bottom: 0,
    padding: 16,
    backgroundColor: "#fff",
    borderTopWidth: 1,
    borderTopColor: "#E5E7EB",
  },
  continueBtn: {
    backgroundColor: "#111",
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: "center",
  },
  continueText: { color: "#fff", fontSize: 16, fontWeight: "600" },
  radioContainer: {
    marginLeft: 12,
  },
  radioButton: {
    width: 20,
    height: 20,
    borderRadius: 10,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#D1D1D1',
    alignSelf: "center",
  },
  specialRow: {
    flexDirection: "row",
    /** Top-align so multi-line titles (e.g. Spanish) are not clipped vs icons */
    alignItems: "flex-start",
    paddingVertical: 16,
    // borderWidth: 1,
    // borderColor: "#E5E7EB",
    // borderRadius: 8,
    // marginBottom: 12,
    // paddingHorizontal: 12,
    // backgroundColor: "#FAFAFA",
  },
  specialRowSelected: {
    borderColor: Colors.black,
    backgroundColor: "#F5F5F5",
  },
  rateRow: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: 4,
  },
  providerLogo: {
    width: 28,
    height: 28,
    borderRadius: 14,
    marginRight: 4,
  },
  arrowIcon: {
    width: 20,
    height: 20,
    tintColor: Colors.titleTextColor,
  },
  countryRow: {
    backgroundColor: Colors.white,
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 16,
    borderBottomWidth: 0,
    justifyContent: 'space-between'
  },
  flag: {
    fontSize: 22,
    marginRight: 12,
  },
  countryName: {
    fontSize: 16,
    fontWeight: "400",
    fontFamily: 'Rubik',
    lineHeight: 20,
    color: Colors.darkGrey,
    paddingLeft: 16
  },
});